import React, { useState, useEffect, useRef } from 'react';
import {
  PlusCircle,
  Edit2,
  Trash2,
  X,
  Image as ImageIcon,
  Calendar,
  User,
  Upload,
  Tag,
  Link as LinkIcon,
  ChevronLeft,
  ChevronRight,
  Bold,
  Italic,
  Underline,
  FileText,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Undo2,
  Redo2,
  Eraser,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Palette,
  PaintBucket,
  ImagePlus
} from 'lucide-react';
import englishService from '../services/englishService';

const Alert = ({ children, variant, className = '' }) => {
  const baseStyles = 'flex w-full rounded-lg border p-4';
  const variantStyles = variant === 'destructive'
    ? 'border-red-200 bg-red-50 text-red-600'
    : 'border-gray-200 bg-gray-50 text-gray-600';

  return (
    <div className={`${baseStyles} ${variantStyles} ${className}`}>
      {children}
    </div>
  );
};

const AlertDescription = ({ children, className = '' }) => (
  <div className={`text-sm ${className}`}>
    {children}
  </div>
);

const BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'UL', 'OL', 'FIGURE']);
const SAVED_BLOCK_REGEX = /^\s*<(p|h2|h3|blockquote|ul|ol|figure)\b/i;
const FONT_SIZE_MAP = {
  1: '12px',
  2: '14px',
  3: '16px',
  4: '18px',
  5: '24px',
  6: '32px',
  7: '40px'
};

const escapeHtml = (value = '') => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const isSafeUrl = (value = '') => /^(https?:\/\/|mailto:)/i.test(value);
const isSafeImageSrc = (value = '') => /^(https?:\/\/|data:image\/(png|jpe?g|gif|webp);base64,)/i.test(value);

const sanitizeCssValue = (value = '') => {
  const trimmed = value.trim();
  return /^[#(),.%\w\s-]+$/.test(trimmed) ? trimmed : '';
};

const getStyleAttribute = (node, properties = ['color', 'backgroundColor', 'fontSize']) => {
  const declarations = [];

  properties.forEach((property) => {
    const value = sanitizeCssValue(node.style?.[property] || '');
    if (!value) return;

    const cssName = property.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
    declarations.push(`${cssName}: ${value}`);
  });

  if (node.tagName === 'FONT') {
    const fontSize = FONT_SIZE_MAP[node.getAttribute('size')];
    const color = sanitizeCssValue(node.getAttribute('color') || '');
    if (fontSize) declarations.push(`font-size: ${fontSize}`);
    if (color) declarations.push(`color: ${color}`);
  }

  return declarations.length ? ` style="${escapeHtml(declarations.join('; '))}"` : '';
};

const wrapStyledInline = (node, children) => {
  const styleAttribute = getStyleAttribute(node);
  return styleAttribute ? `<span${styleAttribute}>${children}</span>` : children;
};

const getBlockAttributes = (node) => {
  const textAlign = sanitizeCssValue(node.style?.textAlign || node.getAttribute('align') || '');
  return ['left', 'center', 'right', 'justify'].includes(textAlign)
    ? ` style="text-align: ${textAlign}"`
    : '';
};

const contentToEditorHtml = (content = []) => {
  const paragraphs = content.filter(paragraph => paragraph?.trim());
  if (!paragraphs.length) return '<p><br></p>';
  return paragraphs
    .map(paragraph => SAVED_BLOCK_REGEX.test(paragraph) ? paragraph : `<p>${paragraph}</p>`)
    .join('');
};

const serializeInlineContent = (node) => {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeHtml(node.textContent.replace(/\u00a0/g, ' '));
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }

  const children = Array.from(node.childNodes).map(serializeInlineContent).join('');
  const tagName = node.tagName;

  if (tagName === 'BR') return '\n';
  if (tagName === 'IMG') {
    const src = node.getAttribute('src') || '';
    const alt = node.getAttribute('alt') || '';
    return isSafeImageSrc(src)
      ? `<figure><img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" /></figure>`
      : '';
  }
  if (tagName === 'B' || tagName === 'STRONG') return `<b>${wrapStyledInline(node, children)}</b>`;
  if (tagName === 'I' || tagName === 'EM') return `<i>${wrapStyledInline(node, children)}</i>`;
  if (tagName === 'U') return `<u>${wrapStyledInline(node, children)}</u>`;
  if (tagName === 'A') {
    const href = node.getAttribute('href') || '';
    return isSafeUrl(href)
      ? `<a href="${escapeHtml(href)}">${wrapStyledInline(node, children)}</a>`
      : children;
  }
  if (tagName === 'SPAN' || tagName === 'FONT') return wrapStyledInline(node, children);

  return children;
};

const serializeBlockContent = (node) => {
  const children = Array.from(node.childNodes).map(serializeInlineContent).join('').trim();
  const tagName = node.tagName;

  if (!children && tagName !== 'UL' && tagName !== 'OL') return '';
  if (tagName === 'FIGURE') return children;
  if (tagName === 'H2') return `<h2${getBlockAttributes(node)}>${children}</h2>`;
  if (tagName === 'H3') return `<h3${getBlockAttributes(node)}>${children}</h3>`;
  if (tagName === 'BLOCKQUOTE') return `<blockquote${getBlockAttributes(node)}>${children}</blockquote>`;
  if (tagName === 'UL' || tagName === 'OL') {
    const items = Array.from(node.children)
      .filter(child => child.tagName === 'LI')
      .map(child => `<li>${Array.from(child.childNodes).map(serializeInlineContent).join('').trim()}</li>`)
      .filter(item => item !== '<li></li>')
      .join('');

    return items ? `<${tagName.toLowerCase()}${getBlockAttributes(node)}>${items}</${tagName.toLowerCase()}>` : '';
  }

  return `<p${getBlockAttributes(node)}>${children}</p>`;
};

const htmlToContent = (html = '') => {
  const container = document.createElement('div');
  container.innerHTML = html;
  const paragraphs = [];
  let inlineBuffer = '';

  Array.from(container.childNodes).forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(node.tagName)) {
      if (inlineBuffer.trim()) {
        paragraphs.push(inlineBuffer.trim());
        inlineBuffer = '';
      }

      const paragraph = serializeBlockContent(node);
      if (paragraph) {
        paragraphs.push(paragraph);
      }
      return;
    }

    inlineBuffer += serializeInlineContent(node);
  });

  if (inlineBuffer.trim()) {
    paragraphs.push(inlineBuffer.trim());
  }

  return paragraphs.map(paragraph => paragraph.trim()).filter(Boolean);
};

const ArticleManagement = () => {
  const [articles, setArticles] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 9
  });
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const contentEditorRef = useRef(null);
  const contentImageInputRef = useRef(null);
  const [contentEditorHtml, setContentEditorHtml] = useState(contentToEditorHtml(['']));

  const [formData, setFormData] = useState({
    title: '',
    thumbnail: '',
    content: [''],
    AnyLink: '',
    date: new Date().toISOString().split('T')[0],
    author: '',
    tags: [],
    images: [],
    pdfReport: ''
  });
  const [pdfUploading, setPdfUploading] = useState(false);

  useEffect(() => {
    fetchArticles(1);
  }, []);

  const fetchArticles = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const response = await englishService.articles.getAll({ 
        page, 
        limit: 9 
      });
      
      if (response.success && response.data) {
        setArticles(response.data);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setError('Invalid response format');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Failed to fetch articles: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchArticles(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    if (name === 'tags') {
      const tagsArray = value.split(',').map(tag => tag.trim()).filter(Boolean);
      setFormData(prev => ({ ...prev, tags: tagsArray }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value || '' }));
    }
  };

  const syncEditorContent = () => {
    const nextContent = htmlToContent(contentEditorRef.current?.innerHTML || '');
    setFormData(prev => ({ ...prev, content: nextContent.length ? nextContent : [''] }));
  };

  const applyInlineFormat = (command) => {
    contentEditorRef.current?.focus();
    document.execCommand(command, false, null);
    syncEditorContent();
  };

  const applyBlockFormat = (tagName) => {
    contentEditorRef.current?.focus();
    document.execCommand('formatBlock', false, tagName);
    syncEditorContent();
  };

  const applyListFormat = (command) => {
    contentEditorRef.current?.focus();
    document.execCommand(command, false, null);
    syncEditorContent();
  };

  const applyLinkFormat = () => {
    const url = window.prompt('Enter link URL');
    if (!url) return;

    const href = /^(https?:\/\/|mailto:)/i.test(url) ? url : `https://${url}`;
    contentEditorRef.current?.focus();
    document.execCommand('createLink', false, href);
    syncEditorContent();
  };

  const removeFormatting = () => {
    contentEditorRef.current?.focus();
    document.execCommand('removeFormat', false, null);
    document.execCommand('unlink', false, null);
    syncEditorContent();
  };

  const applyFontSize = (size) => {
    contentEditorRef.current?.focus();
    document.execCommand('fontSize', false, size);
    syncEditorContent();
  };

  const applyColor = (command, color) => {
    contentEditorRef.current?.focus();
    document.execCommand(command, false, color);
    syncEditorContent();
  };

  const insertContentImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Only image files can be inserted into article content');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Inline content image size should be less than 2MB');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      contentEditorRef.current?.focus();
      const altText = escapeHtml(file.name.replace(/\.[^/.]+$/, ''));
      document.execCommand(
        'insertHTML',
        false,
        `<figure><img src="${reader.result}" alt="${altText}" /></figure><p><br></p>`
      );
      syncEditorContent();
      e.target.value = '';
    };
    reader.readAsDataURL(file);
  };

  const handleContentPaste = (e) => {
    e.preventDefault();

    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    const pastedContent = html
      ? htmlToContent(html)
      : text.split(/\n+/).map(line => line.trim()).filter(Boolean);

    document.execCommand('insertHTML', false, contentToEditorHtml(pastedContent));
    syncEditorContent();
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError(`${type === 'thumbnail' ? 'Thumbnail' : 'Image'} size should be less than 5MB`);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'thumbnail') {
          setFormData(prev => ({ ...prev, thumbnail: reader.result }));
        } else if (type === 'image') {
          if (formData.images.length >= 5) {
            setError('Maximum 5 images are allowed');
            return;
          }
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            const MAX_WIDTH = 1200;
            const MAX_HEIGHT = 1200;
            
            let width = img.width;
            let height = img.height;
            
            if (width > height) {
              if (width > MAX_WIDTH) {
                height *= MAX_WIDTH / width;
                width = MAX_WIDTH;
              }
            } else {
              if (height > MAX_HEIGHT) {
                width *= MAX_HEIGHT / height;
                height = MAX_HEIGHT;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);
            
            const compressedImage = canvas.toDataURL('image/jpeg', 0.7);
            
            setFormData(prev => ({
              ...prev,
              images: [...prev.images, compressedImage]
            }));
          };
          img.src = reader.result;
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    setError('');

    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }

    if (!formData.thumbnail) {
      setError('Thumbnail is required');
      return false;
    }

    if (!formData.content.some(paragraph => paragraph.trim())) {
      setError('At least one content paragraph is required');
      return false;
    }

    if (formData.images && formData.images.length > 5) {
      setError('Maximum 5 images are allowed');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
  
    try {
      setLoading(true);
      setError('');
  
      const formDataToSend = {
        title: formData.title.trim(),
        thumbnail: formData.thumbnail,
        content: formData.content.filter(paragraph => paragraph.trim() !== ''),
        AnyLink: formData.AnyLink || '',
        date: new Date(formData.date).toISOString(),
        author: formData.author?.trim() || '',
        tags: formData.tags.filter(tag => tag.trim() !== ''),
        images: formData.images || [],
        pdfReport: formData.pdfReport || ''
      };
  
      let response;
      if (selectedArticle) {
        response = await englishService.articles.update(selectedArticle._id, formDataToSend);
      } else {
        response = await englishService.articles.create(formDataToSend);
      }
  
      if (response.success) {
        setSuccess(selectedArticle ? 'Article updated successfully!' : 'Article created successfully!');
        await fetchArticles(pagination.currentPage);
        setShowModal(false);
        resetForm();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(response.message || 'Failed to save article');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setError(error.message || 'Failed to save article. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (article) => {
    setSelectedArticle(article);
    setContentEditorHtml(contentToEditorHtml(article.content));
    setFormData({
      title: article.title,
      content: article.content,
      AnyLink: article.AnyLink || '',
      thumbnail: article.thumbnail,
      date: new Date(article.date).toISOString().split('T')[0],
      author: article.author || '',
      tags: article.tags || [],
      images: article.images || [],
      pdfReport: article.pdfReport || ''
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await englishService.articles.delete(selectedArticle._id);
      setSuccess('Article deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
      
      // If current page becomes empty after delete, go to previous page
      if (articles.length === 1 && pagination.currentPage > 1) {
        fetchArticles(pagination.currentPage - 1);
      } else {
        fetchArticles(pagination.currentPage);
      }
      
      setShowDeleteModal(false);
      setSelectedArticle(null);
    } catch (error) {
      setError(error.message || 'Failed to delete article');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setContentEditorHtml(contentToEditorHtml(['']));
    setFormData({
      title: '',
      thumbnail: '',
      content: [''],
      AnyLink: '',
      date: new Date().toISOString().split('T')[0],
      author: '',
      tags: [],
      images: [],
      pdfReport: ''
    });
    setSelectedArticle(null);
  };

  // Handle PDF upload
  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Only PDF files are allowed');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setError('PDF size should be less than 50MB');
      return;
    }

    try {
      setPdfUploading(true);
      const formData = new FormData();
      formData.append('pdf', file);

      const response = await fetch(
        process.env.NODE_ENV === 'production' ? '/api/pdf/upload' : 'http://localhost:5500/api/pdf/upload',
        {
          method: 'POST',
          body: formData,
        }
      );

      const result = await response.json();

      if (result.success) {
        setFormData(prev => ({ ...prev, pdfReport: result.data.path }));
        setSuccess('PDF uploaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(result.message || 'Failed to upload PDF');
      }
    } catch (error) {
      console.error('PDF upload error:', error);
      setError(error.message || 'Failed to upload PDF');
    } finally {
      setPdfUploading(false);
    }
  };

  // Handle PDF removal
  const handleRemovePdf = async () => {
    if (!formData.pdfReport) return;

    try {
      const response = await fetch(
        process.env.NODE_ENV === 'production' ? '/api/pdf/delete' : 'http://localhost:5500/api/pdf/delete',
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pdfPath: formData.pdfReport }),
        }
      );

      const result = await response.json();
      if (result.success) {
        setFormData(prev => ({ ...prev, pdfReport: '' }));
      }
    } catch (error) {
      console.error('PDF delete error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Article Management</h1>
          <p className="text-sm text-gray-600 mt-1">
            Total: {pagination.totalItems} articles
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
        >
          <PlusCircle className="mr-2" size={20} />
          Add Article
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 bg-green-50 text-green-700 border-green-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Card Grid Layout */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">No articles found. Create one to get started!</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <div key={article._id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="h-48 overflow-hidden">
                  <img
                    src={article.thumbnail || '/api/placeholder/400/200'}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="p-4">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">{article.title}</h3>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-gray-600">
                      <User size={16} className="mr-2" />
                      {article.author || 'Anonymous'}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Calendar size={16} className="mr-2" />
                      {new Date(article.date).toLocaleDateString()}
                    </div>
                    {article.tags.length > 0 && (
                      <div className="flex items-center text-gray-600">
                        <Tag size={16} className="mr-2" />
                        {article.tags.join(', ')}
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <p className="text-gray-600 line-clamp-3">
                      {article.content[0]?.replace(/<[^>]+>/g, '')}
                    </p>
                  </div>

                  <div className="flex justify-between items-center">
                    {article.AnyLink ? (
                      <a
                        href={article.AnyLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-sky-500 hover:text-sky-600"
                      >
                        <LinkIcon size={16} className="mr-1" />
                        View Link
                      </a>
                    ) : (
                      <div></div>
                    )}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(article)}
                        className="p-2 text-sky-500 hover:bg-sky-50 rounded-full transition-colors"
                      >
                        <Edit2 size={20} />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedArticle(article);
                          setShowDeleteModal(true);
                        }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center items-center mt-8 space-x-4">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={20} />
              </button>
              
              <div className="flex items-center space-x-2">
                {[...Array(pagination.totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  // Show first page, last page, current page, and adjacent pages
                  if (
                    pageNumber === 1 ||
                    pageNumber === pagination.totalPages ||
                    (pageNumber >= pagination.currentPage - 1 && pageNumber <= pagination.currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`px-4 py-2 rounded-lg ${
                          pagination.currentPage === pageNumber
                            ? 'bg-sky-500 text-white'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (
                    pageNumber === pagination.currentPage - 2 ||
                    pageNumber === pagination.currentPage + 2
                  ) {
                    return <span key={pageNumber} className="px-2">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {selectedArticle ? 'Edit Article' : 'Add Article'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author
                  </label>
                  <input
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thumbnail *
                  </label>
                  <div className="flex items-center space-x-4">
                    {formData.thumbnail && (
                      <img
                        src={formData.thumbnail}
                        alt="Thumbnail preview"
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <label className="flex flex-col items-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-sky-500">
                        <ImageIcon className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">Upload thumbnail</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, 'thumbnail')}
                          required={!formData.thumbnail}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content *
                  </label>
                  <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-sky-500 focus-within:border-sky-500">
                    <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-50 border-b border-gray-300">
                      <button
                        type="button"
                        onClick={() => applyBlockFormat('p')}
                        className="px-3 py-2 text-sm hover:bg-gray-200 rounded transition-colors"
                        title="Paragraph"
                      >
                        P
                      </button>
                      <button
                        type="button"
                        onClick={() => applyBlockFormat('h2')}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Heading"
                      >
                        <Heading2 size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => applyBlockFormat('h3')}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Subheading"
                      >
                        <Heading3 size={16} />
                      </button>
                      <span className="mx-1 h-6 w-px bg-gray-300" />
                      <button
                        type="button"
                        onClick={() => applyInlineFormat('bold')}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Bold"
                      >
                        <Bold size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => applyInlineFormat('italic')}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Italic"
                      >
                        <Italic size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => applyInlineFormat('underline')}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Underline"
                      >
                        <Underline size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={applyLinkFormat}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Link"
                      >
                        <LinkIcon size={16} />
                      </button>
                      <span className="mx-1 h-6 w-px bg-gray-300" />
                      <select
                        defaultValue="3"
                        onChange={(e) => applyFontSize(e.target.value)}
                        className="h-9 rounded border border-gray-300 bg-white px-2 text-sm"
                        title="Font size"
                      >
                        <option value="2">Small</option>
                        <option value="3">Normal</option>
                        <option value="4">Medium</option>
                        <option value="5">Large</option>
                        <option value="6">Huge</option>
                      </select>
                      <label className="flex h-9 items-center gap-1 rounded border border-gray-300 bg-white px-2 text-sm cursor-pointer hover:bg-gray-100" title="Text color">
                        <Palette size={16} />
                        <input
                          type="color"
                          className="h-5 w-6 cursor-pointer border-0 bg-transparent p-0"
                          onChange={(e) => applyColor('foreColor', e.target.value)}
                        />
                      </label>
                      <label className="flex h-9 items-center gap-1 rounded border border-gray-300 bg-white px-2 text-sm cursor-pointer hover:bg-gray-100" title="Background color">
                        <PaintBucket size={16} />
                        <input
                          type="color"
                          className="h-5 w-6 cursor-pointer border-0 bg-transparent p-0"
                          onChange={(e) => applyColor('hiliteColor', e.target.value)}
                        />
                      </label>
                      <span className="mx-1 h-6 w-px bg-gray-300" />
                      <button
                        type="button"
                        onClick={() => applyInlineFormat('justifyLeft')}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Align left"
                      >
                        <AlignLeft size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => applyInlineFormat('justifyCenter')}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Align center"
                      >
                        <AlignCenter size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => applyInlineFormat('justifyRight')}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Align right"
                      >
                        <AlignRight size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => applyInlineFormat('justifyFull')}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Justify"
                      >
                        <AlignJustify size={16} />
                      </button>
                      <span className="mx-1 h-6 w-px bg-gray-300" />
                      <button
                        type="button"
                        onClick={() => applyListFormat('insertUnorderedList')}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Bulleted list"
                      >
                        <List size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => applyListFormat('insertOrderedList')}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Numbered list"
                      >
                        <ListOrdered size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => applyBlockFormat('blockquote')}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Quote"
                      >
                        <Quote size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => contentImageInputRef.current?.click()}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Insert image inside content"
                      >
                        <ImagePlus size={16} />
                      </button>
                      <input
                        ref={contentImageInputRef}
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={insertContentImage}
                      />
                      <span className="mx-1 h-6 w-px bg-gray-300" />
                      <button
                        type="button"
                        onClick={() => applyInlineFormat('undo')}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Undo"
                      >
                        <Undo2 size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => applyInlineFormat('redo')}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Redo"
                      >
                        <Redo2 size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={removeFormatting}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="Clear formatting"
                      >
                        <Eraser size={16} />
                      </button>
                    </div>
                    <div
                      ref={contentEditorRef}
                      contentEditable
                      suppressContentEditableWarning
                      role="textbox"
                      aria-label="Article content"
                      onInput={syncEditorContent}
                      onPaste={handleContentPaste}
                      className="w-full min-h-[360px] p-4 text-gray-800 leading-relaxed outline-none overflow-y-auto [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-5 [&_h2]:mb-3 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-1 [&_blockquote]:border-l-4 [&_blockquote]:border-sky-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_figure]:my-4 [&_figure_img]:max-h-96 [&_figure_img]:max-w-full [&_figure_img]:rounded-lg [&_figure_img]:object-contain"
                      data-placeholder="Write or paste the complete article here..."
                      dangerouslySetInnerHTML={{ __html: contentEditorHtml }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Any Link
                  </label>
                  <input
                    type="text"
                    name="AnyLink"
                    value={formData.AnyLink}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags.join(', ')}
                    onChange={handleInputChange}
                    placeholder="Enter tags separated by commas"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Images (Max 5)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image}
                          alt={`Additional ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              images: prev.images.filter((_, i) => i !== index)
                            }));
                          }}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                    {formData.images.length < 5 && (
                      <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-sky-500">
                        <Upload className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">Add image</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, 'image')}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* PDF Report Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PDF Report (Optional)
                  </label>
                  <div className="flex items-center space-x-4">
                    {formData.pdfReport ? (
                      <div className="flex items-center space-x-3 p-3 bg-green-50 border border-green-200 rounded-lg flex-1">
                        <FileText className="text-green-600" size={24} />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-800">PDF Uploaded</p>
                          <p className="text-xs text-green-600 truncate">{formData.pdfReport}</p>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemovePdf}
                          className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-sky-500 flex-1">
                        {pdfUploading ? (
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
                        ) : (
                          <>
                            <FileText className="text-gray-400 mb-2" size={32} />
                            <span className="text-sm text-gray-600">Upload PDF Report</span>
                            <span className="text-xs text-gray-400 mt-1">Max 50MB</span>
                          </>
                        )}
                        <input
                          type="file"
                          className="hidden"
                          accept="application/pdf"
                          onChange={handlePdfUpload}
                          disabled={pdfUploading}
                        />
                      </label>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : (selectedArticle ? 'Update Article' : 'Create Article')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Delete Article</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedArticle?.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleManagement;
